import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Facebook, Instagram, Mail, Phone } from 'lucide-react';
import { Button } from './ui/button';
import useServices from '../stores/useServices';
import useCompanyProfile from '../stores/useCompanyProfiles';
import Separator from './ui/separator';
import Link from './ui/Link';
import { LanguageContext } from '../contexts/LanguageContext';
import { ABOUT_PAGE, CONTACT_PAGE } from '../routes/routeConstant';

export default function Footer() {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const lang = language === 'id';
  const { fetchServices, services } = useServices();
  const { fetchProfile, profile, loading } = useCompanyProfile();
  const phoneNumber = profile?.whatsapp_pr;
  const message = lang
    ? `Halo, saya tertarik dengan layanan Anda. Mohon informasi lebih lanjut mengenai pilihan layanan, harga, dan ketersediaan. Terima kasih!`
    : `Hello, I am interested in your services. Could you please provide more details about the available options, pricing, and availability? Thank you!`;
  const whatsappUrl = `https://wa.me/+62${phoneNumber}?text=${encodeURIComponent(message)}`;

  useEffect(() => {
    if (services.length === 0) fetchServices();
    if (!profile) fetchProfile();
  }, [services, fetchServices, fetchProfile, profile]);

  return (
    <>
      {loading && !profile && (
        <div className="fixed inset-0 z-50 flex h-screen w-screen items-center justify-center bg-white">
          <div className="flex animate-pulse aspect-square size-32 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
            <img src="/assets/MSA-Logo.jpg" alt="MSA-Logo" className="rounded w-full h-full object-cover" />
          </div>
        </div>
      )}
      <footer className="h-fit mt-4 text-white md:text-sm text-xs transition-all duration-300 bg-primary">
        <div className="p-4 max-w-screen-xl mx-auto xl:grid-cols-4 grid md:grid-cols-2 grid-cols-1 gap-4">
          <div className="location flex flex-col gap-2">
            <h4 className="md:text-lg text-md font-semibold">{t('footer.location.title')}</h4>
            <div className="flex flex-col gap-2">
              <p>{profile?.location_pr || t('footer.location.default_address')}</p>
              <Link
                to={profile?.map_url || ''}
                target="_blank"
                className="flex gap-1 items-center hover:text-secondary"
              >
                {t('footer.location.view_map')} <ArrowRight className="h-4 md:h-5" />
              </Link>
            </div>
          </div>
          <div className="about flex flex-col gap-2">
            <h4 className="md:text-lg text-md font-semibold">{t('footer.about.title')}</h4>
            <ul className="flex flex-col gap-1">
              <li>
                <Link to={ABOUT_PAGE} className="hover:text-secondary">
                  {t('footer.about.about_us')}
                </Link>
              </li>
              <li>
                <Link to={CONTACT_PAGE} className="hover:text-secondary">
                  {t('footer.about.contact_us')}
                </Link>
              </li>
            </ul>
          </div>
          <div className="services flex flex-col gap-2">
            <h4 className="md:text-lg text-md font-semibold">{t('footer.services.title')}</h4>
            <ul className="flex flex-col gap-1">
              {services.slice(0, 8).map((srv) => (
                <li key={srv.service_id}>
                  <Link to={`/service/${srv.service_id}`} className="hover:text-secondary">
                    {lang ? srv.name_srv_id : srv.name_srv_en || srv.name_srv_id}
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/services" className="flex gap-1 items-center hover:text-secondary">
              {t('footer.services.see_more')} <ArrowRight className="h-4 md:h-5" />
            </Link>
          </div>
          <div className="contact flex flex-col gap-2">
            <h4 className="md:text-lg text-md font-semibold">{t('footer.contact.title')}</h4>
            <ul className="flex flex-col gap-1">
              <li>
                <Button
                  variant="link"
                  className="text-white hover:no-underline hover:cursor-text select-text h-fit text-xs md:text-sm p-0"
                >
                  <Phone />
                  {profile?.phone_pr || ''}
                </Button>
              </li>
              <li>
                <Link to={whatsappUrl} target="_blank">
                  <Button
                    variant="link"
                    className="text-white hover:text-secondary hover:no-underline h-fit text-xs md:text-sm p-0"
                  >
                    <img alt="whatsapp logo" src="/assets/Whatsapp.svg" className="w-4" />
                    +62{profile?.whatsapp_pr || ''}
                  </Button>
                </Link>
              </li>
              <li>
                <Button
                  variant="link"
                  className="text-white hover:no-underline hover:cursor-text select-text h-fit text-xs md:text-sm p-0"
                >
                  <Mail />
                  {profile?.email_pr || ''}
                </Button>
              </li>
            </ul>
            <h4 className="md:text-lg text-md font-semibold">{t('footer.contact.follow_us')}</h4>
            <div className="flex gap-1 w-full">
              {profile?.ig_pr && (
                <Link
                  to={profile?.ig_pr}
                  target="_blank"
                  className="p-1 md:p-2 border border-border rounded-md hover:bg-neutral-900/50"
                >
                  <Instagram className="md:h-6 h-5" />
                </Link>
              )}{' '}
              {profile?.fb_pr && (
                <Link
                  to={profile?.fb_pr}
                  target="_blank"
                  className="p-1 md:p-2 border border-border rounded-md hover:bg-neutral-900/50"
                >
                  <Facebook className="md:h-6 h-5" />
                </Link>
              )}{' '}
              {profile?.x_pr && (
                <Link
                  to={profile?.x_pr}
                  target="_blank"
                  className="p-1 md:p-2 border border-border rounded-md hover:bg-neutral-900/50"
                >
                  <img alt="twitter" src="/assets/XTwitter.svg" className="md:h-6 h-5" />
                </Link>
              )}{' '}
              {profile?.tiktok_pr && (
                <Link
                  to={profile?.tiktok_pr}
                  target="_blank"
                  className="p-1 md:p-2 border border-border rounded-md hover:bg-neutral-900/50"
                >
                  <img alt="tiktok" src="/assets/Tiktok.svg" className="md:h-6 h-5" />
                </Link>
              )}
            </div>
          </div>
        </div>
        <Separator />
        <div className="p-4 max-w-screen-xl mx-auto">
          <p>© {new Date().getFullYear()} PT. Mulia Sujar Abadi. All Rights Reserved.</p>
        </div>
      </footer>
    </>
  );
}
